/**
 * =========================
 * 광고 유입(adSrc) 공통 유틸
 * =========================
 * 설계서: _workspace/meta_adsrc_creative/01_architect_design.md 2장, 3장 참고
 *
 * - sessionStorage `adSrc` 에 v2 스키마(base64 JSON)를 저장한다.
 * - 사용자 결정: localStorage 백업은 두지 않는다. sessionStorage만 사용한다.
 */

const SESSION_KEY_AD_SRC = 'adSrc';

/** 기존 GENERAL 기본값(`{ "adPath" : "GENERAL"}`의 base64). 레거시 포맷과 호환을 위해 그대로 사용한다. */
export const AD_SRC_GENERAL = 'eyAiYWRQYXRoIiA6ICAiR0VORVJBTCJ9';

/** media/utm_source 별칭 → AdType enum name 매핑 (설계서 3.2) */
export const MEDIA_ALIAS: Record<string, string> = {
  meta: 'META',
  facebook: 'META',
  fb: 'META',
  instagram: 'META',
  ig: 'META',
};

const MEDIA_PATTERN = /^[A-Z0-9_]{1,30}$/;
// tsconfig target이 es5라 정규식 `u` 플래그(\p{L}\p{N})를 쓸 수 없다.
// 영문/숫자/한글(자모+완성형)을 직접 문자 범위로 명시해 동일한 효과를 낸다.
const TOKEN_PATTERN = /^[0-9A-Za-z_.\-ㄱ-ㆎ가-힣]{1,100}$/;
const TOKEN_MAX_LENGTH = 100;

export interface CaptureAdAttributionOptions {
  /**
   * true면 레거시 `ad_src` 파라미터도 last-click으로 덮어쓴다.
   * (media/utm_source가 있을 때의 v2 캡처는 설계서 3.3-1에 따라 force 여부와 무관하게 항상 덮어쓴다)
   */
  force?: boolean;
}

/**
 * 안전하게 sessionStorage를 읽는다. 프라이빗 모드 등에서 접근이 막혀도 예외를 던지지 않는다.
 */
function getSessionAdSrc(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_KEY_AD_SRC);
  } catch (e) {
    return null;
  }
}

/**
 * 안전하게 sessionStorage에 쓴다. 프라이빗 모드 등에서 접근이 막혀도 예외를 던지지 않는다.
 */
export function setSessionAdSrc(value: string): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY_AD_SRC, value);
  } catch (e) {
    // sessionStorage 접근 불가 시 조용히 무시한다.
  }
}

/**
 * media/utm_source 값을 정규화한다.
 * 1) 소문자 변환 후 별칭맵(MEDIA_ALIAS) 적용
 * 2) 별칭에 없으면 toUpperCase().replace(/-/g,'_')
 * 3) `^[A-Z0-9_]{1,30}$` 검사 실패 시 null
 */
export function normalizeMedia(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }

  const lower = raw.trim().toLowerCase();
  if (!lower) {
    return null;
  }

  const aliased = MEDIA_ALIAS[lower];
  const candidate = aliased ?? lower.toUpperCase().replace(/-/g, '_');

  return MEDIA_PATTERN.test(candidate) ? candidate : null;
}

/**
 * campaignId(소재) sanitize.
 * trim → 공백류는 `_`로 치환 → 100자 초과 시 자르기 → 영문/숫자/한글/`_`/`.`/`-` 조합(1~100자) 검사, 실패하면 null.
 */
export function sanitizeToken(raw: string | null | undefined): string | null {
  if (raw == null) {
    return null;
  }

  let value = raw.trim();
  if (!value) {
    return null;
  }

  value = value.replace(/\s+/g, '_');
  if (value.length > TOKEN_MAX_LENGTH) {
    value = value.substring(0, TOKEN_MAX_LENGTH);
  }

  return TOKEN_PATTERN.test(value) ? value : null;
}

/**
 * UTF-8로 인코딩한 JSON을 base64로 변환한다 (한글 깨짐 방지).
 * 그냥 btoa(JSON.stringify(...))를 쓰면 한글 등 멀티바이트 문자에서 예외가 발생한다.
 */
export function encodeAdSrc(payload: Record<string, unknown>): string {
  const json = JSON.stringify(payload);
  return window.btoa(unescape(encodeURIComponent(json)));
}

/**
 * base64 adSrc(v1/v2 공용)를 디코드한다. 실패하면 null을 반환한다.
 * 레거시 URL에서 공백으로 바뀐 `+`도 복원한다.
 */
export function decodeAdSrc(base64: string | null | undefined): Record<string, unknown> | null {
  if (!base64) {
    return null;
  }

  try {
    const normalized = base64.replace(/ /g, '+');
    const json = decodeURIComponent(escape(window.atob(normalized)));
    const parsed = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch (e) {
    return null;
  }
}

/**
 * URL 쿼리파라미터로부터 유입 정보를 캡처해 sessionStorage `adSrc`에 저장한다.
 * `index.js`(모든 진입 URL)와 `AdLandingPage`(SPA 내부 이동 대응) 양쪽에서 호출한다.
 * 여러 번 호출해도 결과가 같다(설계서 3.3).
 *
 * 1) media/utm_source가 있고 정규화에 성공하면 v2 adSrc를 만들어 무조건 덮어쓴다(last-click).
 * 2) 1이 아니고 레거시 `ad_src`가 있으면, sessionStorage가 비어있을 때만 저장한다.
 *    (force=true면 last-click 정책을 레거시 ad_src에도 동일하게 적용해 덮어쓴다)
 * 3) 그래도 sessionStorage가 비어있으면 GENERAL 기본값을 넣는다.
 */
export function captureAdAttribution(search: string, options: CaptureAdAttributionOptions = {}): void {
  const { force = false } = options;
  const params = new URLSearchParams(search);

  // 관리 링크는 랜딩에서 서버 해석 후 저장한다.
  if (params.has('link')) return;

  const media = normalizeMedia(params.get('media') ?? params.get('utm_source'));
  if (media) {
    // 소재(캠페인) 식별값. campaign/utm_campaign을 우선하고, creative/utm_content도 동일 의미로 허용한다.
    const campaignId = sanitizeToken(
      params.get('campaign') ?? params.get('creative') ?? params.get('utm_campaign') ?? params.get('utm_content')
    );

    const payload: Record<string, unknown> = { adPath: media, v: 2 };
    if (campaignId) {
      payload.campaignId = campaignId;
    }

    setSessionAdSrc(encodeAdSrc(payload));
    return;
  }

  const legacyAdSrc = params.get('ad_src');
  if (legacyAdSrc) {
    if (force || !getSessionAdSrc()) {
      setSessionAdSrc(legacyAdSrc);
    }
    return;
  }

  if (!getSessionAdSrc()) {
    setSessionAdSrc(AD_SRC_GENERAL);
  }
}

/**
 * 회원가입 제출 시 사용할 adSrc 값을 반환한다.
 * sessionStorage에 값이 있으면 그 값을, 없으면 GENERAL 기본값을 반환한다.
 */
export function getAdSrcForSubmit(): string {
  return getSessionAdSrc() || AD_SRC_GENERAL;
}
